
import uniqBy from 'lodash/uniqBy';
import isEmpty from 'lodash/isEmpty';

import { SortTypes, Urls } from '@share/constants';
import { IGetaway, IGetawaysLocations, GetawaysDefaultLocation, GetawaysDefaultLocationCountry, GetawaysUnSpecifiedLocationCountry, defaultLocationCountriesValue, notSpecifiedCountriesValue } from '@share/common-types';
import { getHeaders, axiosInstance, getSelectedCurrency } from '@share/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_NUMBER, USD_CURRENCY } from '@constants';
import { AppThunk } from '@share/utils';
import { GetGetawaysLocationsByIso } from '@utils';
import { get } from 'lodash';

const ZERO = 0;
const ONE = 1;

export interface IGetawaysState {
  getaways: IGetaway[];
  errorGetaways: string;
  loadingGetaways: boolean;
  loadingMoreGetaways: boolean;
  getawaysLocations: IGetawaysLocations[];
  getawaysLocationsOriginal: IGetawaysLocations[];
  getawaysLocationsCountries: IGetawaysLocations[];
  errorGetawaysLocations: string;
  loadingGetawaysLocations: boolean;
  prevGetaways: IGetaway[];
  pageNumber: number;
  pageSize: number;
  getawaysTotalCount: number;
  sortType: SortTypes;
  filterType: IGetawaysLocations;
  filterTypeCountry: IGetawaysLocations;
  promoId: string;
  isGetawaysListPage: boolean;
  activeGetawaySubcategory: string;
}

export const getawaysInitialState: IGetawaysState = {
  getaways: [],
  errorGetaways: '',
  loadingGetaways: false,
  loadingMoreGetaways: false,
  getawaysLocations: [],
  getawaysLocationsOriginal: [],
  getawaysLocationsCountries: [],
  errorGetawaysLocations: '',
  loadingGetawaysLocations: false,
  prevGetaways: [],
  pageNumber: DEFAULT_PAGE_NUMBER,
  pageSize: DEFAULT_PAGE_SIZE,
  getawaysTotalCount: ZERO,
  sortType: SortTypes.PriceAsc,
  filterType: undefined,
  filterTypeCountry: undefined,
  promoId: '',
  isGetawaysListPage: false,
  activeGetawaySubcategory: ''
};

const getawaysSlice = createSlice({
  name: 'getaways',
  initialState: getawaysInitialState,
  reducers: {
    setGetaways: (
      state: IGetawaysState,
      { payload }: PayloadAction<{ getaways: IGetaway[]; isLoadMore: boolean }>,
    ) => {
      state.prevGetaways = payload.getaways;

      if (payload.isLoadMore) {
        state.getaways = [...(state.getaways || []), ...payload.getaways];
      } else {
        state.getaways = payload.getaways;
      }
    },
    setGetawaysTotalCount: (state: IGetawaysState, { payload }: PayloadAction<number>) => {
      state.getawaysTotalCount = payload;
    },
    setLoadingGetaways: (state: IGetawaysState, { payload }: PayloadAction<boolean>) => {
      state.loadingGetaways = payload;
    },
    setLoadingMoreGetaways: (state: IGetawaysState, { payload }: PayloadAction<boolean>) => {
      state.loadingMoreGetaways = payload;
    },
    setErrorGetaways: (state: IGetawaysState, { payload }: PayloadAction<string>) => {
      state.errorGetaways = payload;
    },
    setPageNumber: (state: IGetawaysState, { payload }: PayloadAction<number | undefined>) => {
      state.pageNumber = payload ? payload : state.pageNumber + ONE;
    },
    setPageSize: (state: IGetawaysState, { payload }: PayloadAction<number | undefined>) => {
      state.pageSize = payload;
    },
    setGetawaysLocations: (state: IGetawaysState, { payload }: PayloadAction<IGetawaysLocations[]>) => {
      state.getawaysLocations = payload;
    },
    setGetawaysLocationsOriginal: (state: IGetawaysState, { payload }: PayloadAction<IGetawaysLocations[]>) => {
      state.getawaysLocationsOriginal = payload;
    },
    setGetawaysLocationsCountries: (state: IGetawaysState, { payload }: PayloadAction<IGetawaysLocations[]>) => {
      state.getawaysLocationsCountries = payload;
    },
    setLoadingGetawaysLocations: (state: IGetawaysState, { payload }: PayloadAction<boolean>) => {
      state.loadingGetawaysLocations = payload;
    },
    setErrorGetawaysLocations: (state: IGetawaysState, { payload }: PayloadAction<string>) => {
      state.errorGetawaysLocations = payload;
    },
    setSortType: (state: IGetawaysState, { payload }: PayloadAction<SortTypes>) => {
      state.sortType = payload;
    },
    setPromoId: (state: IGetawaysState, { payload }: PayloadAction<string>) => {
      state.promoId = payload;
    },
    setGetawaysListPage: (state: IGetawaysState, { payload }: PayloadAction<boolean>) => {
      state.isGetawaysListPage = payload;
    },
    setFiltrationType: (state: IGetawaysState, { payload }: PayloadAction<IGetawaysLocations>) => {
      state.filterType = payload;
    },
    setFiltrationTypeCountry: (state: IGetawaysState, { payload }: PayloadAction<IGetawaysLocations>) => {
      state.filterTypeCountry = payload;

      state.getawaysLocations = GetGetawaysLocationsByIso(payload, state.getawaysLocationsOriginal);
    },
    resetFilters: (state: IGetawaysState) => {
      state.filterType = undefined;
      state.filterTypeCountry = undefined;
      state.promoId = '';
    },
    setDefaultValues: (state: IGetawaysState) => {
      return {...getawaysInitialState, activeGetawaySubcategory: state.activeGetawaySubcategory};
    },
    setActiveGetawaySubcategory: (state: IGetawaysState, { payload }: PayloadAction<string>) => {
      state.activeGetawaySubcategory = payload;
    }
  },
});

export const getawaysActions = getawaysSlice.actions;

export const getawaysReducer = getawaysSlice.reducer;

export const getGetaways = (
  getaway: string,
  subCategory: string,
  isLoadMore = false,
  isSearch = false,
): AppThunk => {
  return async (dispatch, getState) => {
    try {
      if (isLoadMore || isSearch) {
        dispatch(getawaysActions.setLoadingMoreGetaways(true));
      } else {
        dispatch(getawaysActions.setLoadingGetaways(true));
      }

      const { getawaysStore, loginStore, navigationMenuStore } = getState();

      const { userCoords, account } = loginStore;
      const { items } = navigationMenuStore;

      const isRSITemplate = account?.isRSITemplate;
      const isMLM = items?.isMLM;
      const hasCord = !!userCoords?.latitude && !!userCoords?.longitude;
      const isMLMDeals = isMLM && isRSITemplate && (hasCord || getawaysStore.filterTypeCountry?.isoCountry);
      
      const currency = getSelectedCurrency(account);

      const payload: any = {
        getaway: getaway,
        subCategory: subCategory,
        sortBy: getawaysStore.sortType,
        location: getawaysStore.filterType,
        promoId: getawaysStore.promoId,
        currency: isEmpty(currency) ? USD_CURRENCY : currency,
        page: {
          size: getawaysStore.pageSize,
          number: getawaysStore.pageNumber,
        },
      };

      if (isMLMDeals) {
        if (hasCord) {
          payload.longitude = userCoords?.longitude;
          payload.latitude = userCoords?.latitude;
        }
        if (!isEmpty(getawaysStore.filterTypeCountry?.isoCountry)) {
          payload.isoCountry = getawaysStore.filterTypeCountry?.isoCountry;
        }
      }

      const url = Urls.Getaways;

      const { data } = await axiosInstance.post(
        url,
        payload,
        { ...getHeaders() },
      );

      dispatch(getawaysActions.setGetaways({ getaways: data.deals, isLoadMore }));
      if (!isLoadMore) {
        dispatch(getawaysActions.setGetawaysTotalCount(data.totalCount));
      }
      dispatch(getawaysActions.setLoadingGetaways(false));
      dispatch(getawaysActions.setLoadingMoreGetaways(false));
    } catch (error) {
      dispatch(getawaysActions.setErrorGetaways(error.toString()));
      dispatch(getawaysActions.setLoadingGetaways(false));
      dispatch(getawaysActions.setLoadingMoreGetaways(false));
    }
  };
};

export const getGetawaysLocations = (getaway: string, subCategory: string): AppThunk => {
  return async (dispatch, getState) => {
    try {
      dispatch(getawaysActions.setLoadingGetawaysLocations(true));

      await handleGetawaysLocations(dispatch, getState, getaway, subCategory, false);

      dispatch(getawaysActions.setLoadingGetawaysLocations(false));
    } catch (error) {
      const code = get(error, 'response.data.code');
      if (['LOCATIONS_NOT_FOUND', 'RSI_IS_NOT_MLM'].includes(code)) {
        await handleGetawaysLocations(dispatch, getState, getaway, subCategory, true);
      } else {
        dispatch(getawaysActions.setErrorGetawaysLocations(error.toString()));
        dispatch(getawaysActions.setLoadingGetawaysLocations(false));
      }
    }
  };
};


export const handleGetawaysLocations = async (dispatch: any, getState: any, getaway: string, subCategory: string, excludeCountries: boolean) => {
  const { loginStore, navigationMenuStore } = getState();

  const { userCoords, account } = loginStore;
  const { items } = navigationMenuStore;

  const isRSITemplate = account?.isRSITemplate;
  const isMLM = items?.isMLM;
  const isMLMDeals = isMLM && isRSITemplate && userCoords?.latitude && userCoords?.longitude;
  
  const url = (isMLMDeals && !excludeCountries) ? 
                      `${Urls.GetawaysMLM}?longitude=${userCoords?.longitude}&latitude=${userCoords?.latitude}` :
                      `${Urls.Getaways}/${getaway}/locations`;

  const { data } = await axiosInstance.get(url, {
    params: {
      subCategory,
    },
    ...getHeaders(),
  });

  if (data) {
    const filtrationList = [GetawaysDefaultLocation, ...data];
    dispatch(getawaysActions.setGetawaysLocations(filtrationList));
    dispatch(getawaysActions.setGetawaysLocationsOriginal(filtrationList));

    if (!excludeCountries) {
      dispatch(handleGetawaysCountries(data));
    }
  }
};

export const handleGetawaysCountries = (data: any[]): AppThunk => {
  return async (dispatch) => {
    const hasSpecifiedCountry = data?.filter((c: any) => !isEmpty(c?.country)).length > 0;

    var dataUnique = uniqBy(data?.filter((c: any) => !isEmpty(c?.country)), 'isoCountry'); 

    const countryList = dataUnique.map((c: any) => ({
      country: c?.country,
      isoCountry: c?.isoCountry,
    }));

    let filtrationCountryList: any = (countryList?.length && hasSpecifiedCountry) ? Array.from(new Set(countryList)).map((c: any) => ({
      country: c?.country,
      isoCountry: c?.isoCountry
    })) : [];
  

    if (filtrationCountryList?.length > 1) {
      filtrationCountryList = [GetawaysDefaultLocationCountry, ...filtrationCountryList];
      dispatch(getawaysActions.setGetawaysLocationsCountries(filtrationCountryList));
    }
  }
}